import jsPDF from "jspdf";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import dayjs from "dayjs";
import { OPTIONS_TYPE } from "../constant/SchemaType";

// ─────────────────────────────────────────────────────────────────────────────
// กลุ่มของ SCHEMA สำหรับออก report (lsfhp + rpsl รวมเป็น report เดียวกัน)
// ─────────────────────────────────────────────────────────────────────────────
const SCHEMA_GROUP = {
  sfhp: "group1",
  vsfhp: "group2",
  lsfhp: "group3",
  rpsl: "group3",
  psfhp: "group3",
};

// ─────────────────────────────────────────────────────────────────────────────
// แปลงตัวเลขเป็นตัวหนังสือภาษาไทย
// ─────────────────────────────────────────────────────────────────────────────
const numberToThaiText = (amount) => {
  const num = Number(amount);
  if (!num || isNaN(num)) return "ศูนย์บาทถ้วน";

  const ones = [
    "",
    "หนึ่ง",
    "สอง",
    "สาม",
    "สี่",
    "ห้า",
    "หก",
    "เจ็ด",
    "แปด",
    "เก้า",
  ];
  const levels = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน", "ล้าน"];

  const toText = (n) => {
    if (n === 0) return "";
    if (n < 10) return ones[n];
    let result = "";
    let remaining = n;
    const digits = [];
    while (remaining > 0) {
      digits.unshift(remaining % 10);
      remaining = Math.floor(remaining / 10);
    }
    for (let i = 0; i < digits.length; i++) {
      const pos = digits.length - 1 - i;
      const d = digits[i];
      if (d === 0) continue;
      if (pos === 1 && d === 1) result += "สิบ";
      else if (pos === 1 && d === 2) result += "ยี่สิบ";
      else result += ones[d] + levels[pos];
    }
    return result;
  };

  const [intPart, decPart] = num.toFixed(2).split(".");
  const intNum = parseInt(intPart);
  const decNum = parseInt(decPart);

  let result = "";
  if (intNum >= 1_000_000) {
    const mil = Math.floor(intNum / 1_000_000);
    const rem = intNum % 1_000_000;
    result = toText(mil) + "ล้าน" + (rem > 0 ? toText(rem) : "");
  } else {
    result = toText(intNum);
  }
  result += "บาท";
  result += decNum > 0 ? toText(decNum) + "สตางค์" : "ถ้วน";
  return result;
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const drawCell = (doc, x, y, w, h, text, align = "left", opts = {}) => {
  const {
    fillColor = null,
    fontSize = 13,
    bold = false,
    font = "THSarabunNew",
    paddingX = 2,
    textColor = [0, 0, 0],
  } = opts;

  if (fillColor) {
    doc.setFillColor(...fillColor);
    doc.rect(x, y, w, h, "F");
  }
  doc.setLineWidth(0.3);
  doc.setDrawColor(0, 0, 0);
  doc.rect(x, y, w, h, "S");

  doc.setFont(font, bold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...textColor);

  const xText =
    align === "right"
      ? x + w - paddingX
      : align === "center"
        ? x + w / 2
        : x + paddingX;

  doc.text(String(text ?? ""), xText, y + h * 0.65, { align });
  doc.setTextColor(0, 0, 0);
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────────────────
export const ExportPaymentInvestigatePDF = ({
  reportData = [],
  selectedCompany = {},
}) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const PW = 210;
  const PH = 297;
  const ML = 10;
  const MR = 10;
  const TW = PW - ML - MR;

  const cols = [
    { label: "ลำดับ", key: "no", w: 12, align: "center" },
    { label: "เลขสัญญา", key: "contno", w: 25, align: "center" },
    { label: "ชื่อลูกค้า", key: "fullName", w: 50, align: "left" },
    { label: "จำนวน/คน", key: "reducerCount", w: 18, align: "center" },
    { label: "ค่าคัดเอกสาร", key: "tr14new", w: 28, align: "center" },
    { label: "ค่าดำเนินการ", key: "operationFeeNew", w: 28, align: "center" },
    { label: "รวม", key: "totalNew", w: 25, align: "right" },
  ];

  const HEADER_H = 15;
  const ROW_H = 8;
  const ROWS_PER = 28;

  const companyName =
    selectedCompany?.label || "บริษัท วัน มันนี่ จำกัด (สำนักงานใหญ่)";

  // ── หา label ของ schema จาก OPTIONS_TYPE ────────────────────────────────
  const schemaLabel = (schema) =>
    OPTIONS_TYPE.find((item) => item.value === schema)?.label ?? schema;

  // ── จัดกลุ่มตาม SCHEMA_GROUP ─────────────────────────────────────────────
  const groupMap = {};
  reportData.forEach((r) => {
    // schema ที่ไม่อยู่ใน SCHEMA_GROUP ให้แยกเป็นกลุ่มของตัวเอง
    const key = SCHEMA_GROUP[r.SCHEMA] ?? r.SCHEMA ?? "ไม่ระบุ";
    if (!groupMap[key]) groupMap[key] = [];
    groupMap[key].push(r);
  });

  // เรียงลำดับกลุ่ม group1 → group2 → group3 → อื่นๆ
  const groupKeys = Object.keys(groupMap).sort();

  let globalPageNum = 0; // นับหน้าจริงทั้ง PDF

  // ── วาด header ของหน้า ───────────────────────────────────────────────────
  const renderPageHeader = (pageInGroup, totalPagesInGroup) => {
    let y = 14;

    doc.setFont("THSarabunNew", "bold");
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(companyName, ML, y);

    doc.setTextColor(150, 150, 150);
    doc.setFontSize(11);
    doc.text(`หน้าที่ ${pageInGroup}/${totalPagesInGroup}`, PW - MR, 10, {
      align: "right",
    });

    y += 6;
    doc.setFont("THSarabunNew", "bold");
    doc.setFontSize(15);
    doc.setTextColor(0, 0, 0);
    doc.text("รายงานแจ้งค่าใช้จ่ายสืบทรัพย์", ML, y);

    y += 10;
    doc.setFont("THSarabunNew", "bold");
    doc.setFontSize(13);
    doc.text(
      "ชื่อทนาย นายธนกฤต มานนท์  เลขที่บัญชี 020417269386  ออมสิน",
      ML,
      y,
    );
    doc.text(`วันที่พิมพ์ ${dayjs().format("DD/MM/YYYY")}`, ML + 140, y);

    y += 5;
    return y;
  };

  // ── วาด table header ─────────────────────────────────────────────────────
  const renderTableHeader = (y) => {
    let x = ML;
    cols.forEach((col) => {
      drawCell(doc, x, y, col.w, HEADER_H, col.label, "center", {
        fillColor: [210, 210, 210],
        bold: true,
        fontSize: 12,
      });
      x += col.w;
    });
    return y + HEADER_H;
  };

  // ── วนตาม GROUP ──────────────────────────────────────────────────────────

  groupKeys.forEach((group) => {
    const list = groupMap[group];

    // เรียงข้อมูลในกลุ่ม: schema ก่อน แล้วค่อย contno
    list.sort(
      (a, b) =>
        String(a.SCHEMA || "").localeCompare(String(b.SCHEMA || "")) ||
        String(a.contno || "").localeCompare(String(b.contno || "")),
    );

    // label ของกลุ่ม เช่น "lsfhp + rpsl"
    const schemasInGroup = [...new Set(list.map((r) => r.SCHEMA))];
    const groupLabel = schemasInGroup.map(schemaLabel).join(" + ");

    const sumTr14new = list.reduce((s, r) => s + Number(r.tr14new || 0), 0);
    const sumOperationFeeNew = list.reduce(
      (s, r) => s + Number(r.operationFeeNew || 0),
      0,
    );
    const sumTotalNew = list.reduce((s, r) => s + Number(r.totalNew || 0), 0);

    const totalPagesInGroup = Math.ceil(list.length / ROWS_PER) || 1;

    for (let p = 0; p < totalPagesInGroup; p++) {
      // ── เพิ่มหน้าใหม่ ──
      if (globalPageNum > 0) doc.addPage();
      globalPageNum++;

      const pageInGroup = p + 1;
      const isLastPageOfGroup = p === totalPagesInGroup - 1;

      let y = renderPageHeader(pageInGroup, totalPagesInGroup);
      y = renderTableHeader(y);

      // ── data rows ──
      const pageData = list.slice(p * ROWS_PER, (p + 1) * ROWS_PER);
      pageData.forEach((row, idx) => {
        const globalIdx = p * ROWS_PER + idx + 1;
        const fullName =
          `${row.SNAM || ""}${row.NAME1 || ""} ${row.NAME2 || ""}`.trim();
        const fill = idx % 2 === 0 ? [255, 255, 255] : [245, 248, 255];

        const rowValues = {
          no: String(globalIdx),
          contno: row.contno || "-",
          fullName,
          reducerCount: row.reducerCount,
          tr14new: fmtMoney(row.tr14new),
          operationFeeNew: fmtMoney(row.operationFeeNew),
          totalNew: fmtMoney(row.totalNew),
        };

        let x = ML;
        cols.forEach((col) => {
          drawCell(
            doc,
            x,
            y,
            col.w,
            ROW_H,
            rowValues[col.key] ?? "",
            col.align,
            {
              fillColor: fill,
              fontSize: 13,
            },
          );
          x += col.w;
        });
        y += ROW_H;
      });

      // ── แถวรวม + signature เฉพาะหน้าสุดท้ายของกลุ่ม ──
      if (isLastPageOfGroup) {
        const groupSumValues = {
          no: "",
          contno: "",
          fullName: `รวม (${groupLabel})`,
          reducerCount: "",
          tr14new: fmtMoney(sumTr14new),
          operationFeeNew: fmtMoney(sumOperationFeeNew),
          totalNew: fmtMoney(sumTotalNew),
        };
        let x = ML;
        cols.forEach((col) => {
          drawCell(
            doc,
            x,
            y,
            col.w,
            ROW_H,
            groupSumValues[col.key] ?? "",
            col.align,
            {
              fillColor: [200, 220, 255],
              bold: true,
              fontSize: 13,
            },
          );
          x += col.w;
        });
        y += ROW_H + 4;

        // จำนวนเงินตัวหนังสือ
        const thaiText = numberToThaiText(sumTotalNew);
        const labelW = 55;
        const valW = TW - labelW;
        drawCell(
          doc,
          ML,
          y,
          labelW,
          ROW_H + 1,
          "จำนวนเงินเป็นตัวหนังสือ",
          "center",
          {
            fillColor: [230, 230, 230],
            bold: true,
            fontSize: 13,
          },
        );
        drawCell(doc, ML + labelW, y, valW, ROW_H + 1, thaiText, "center", {
          fillColor: [255, 255, 255],
          fontSize: 16,
          bold: true,
        });
        y += ROW_H + 1 + 6;

        // หมายเหตุ
        doc.setTextColor(180, 0, 0);
        doc.setFont("THSarabunNew", "normal");
        doc.setFontSize(11);
        doc.text(
          "หมายเหตุ* : ค่าคัดเอกสาร(ทร.14+ทะเบียนครอบครับ), ค่าดำเนินการ(ค่าดำเนินการ+เพิ่มเติม)",
          ML,
          y,
        );
        doc.setTextColor(0, 0, 0);
        y += 20;
        // signature
        const sigY = Math.max(y, PH - 85);
        const sigLineW = 60;
        const sig1Lx = ML + 15;
        const sig1Rx = PW - MR - 15 - sigLineW;

        doc.setLineWidth(0.4);
        doc.setDrawColor(0, 0, 0);
        doc.setFont("THSarabunNew", "normal");
        doc.setFontSize(13);

        doc.line(sig1Lx, sigY, sig1Lx + sigLineW, sigY);
        doc.text("( นายธนกฤต มานนท์ )", sig1Lx + sigLineW / 2, sigY + 5, {
          align: "center",
        });
        doc.text("ทนาย", sig1Lx + sigLineW / 2, sigY + 10, { align: "center" });

        doc.line(sig1Rx, sigY, sig1Rx + sigLineW, sigY);
        doc.text("( มานิตา เมืองจันทร์ )", sig1Rx + sigLineW / 2, sigY + 5, {
          align: "center",
        });
        doc.text("ผู้อนุมัติ", sig1Rx + sigLineW / 2, sigY + 10, {
          align: "center",
        });

        const sigY2 = sigY + 22;
        doc.line(sig1Lx, sigY2, sig1Lx + sigLineW, sigY2);
        doc.text("(", sig1Lx, sigY2 + 5);
        doc.text(")", sig1Lx + sigLineW, sigY2 + 5, { align: "right" });
        doc.text("ผู้ตรวจสอบ", sig1Lx + sigLineW / 2, sigY2 + 15, {
          align: "center",
        });

        doc.line(sig1Rx, sigY2, sig1Rx + sigLineW, sigY2);
        doc.text("(", sig1Rx, sigY2 + 5);
        doc.text(")", sig1Rx + sigLineW, sigY2 + 5, { align: "right" });
        doc.text("ผู้ตรวจสอบ", sig1Rx + sigLineW / 2, sigY2 + 15, {
          align: "center",
        });
      }
    }
  });

  // ── Print ────────────────────────────────────────────────────────────────
  doc.autoPrint();
  const blobUrl = doc.output("bloburl");
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = blobUrl;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    iframe.contentWindow.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  };
};
